import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import QRCode from "react-qr-code";
import { format } from "date-fns";
import { 
  CreditCard, 
  QrCode, 
  Calendar, 
  Store, 
  LogOut,
  User,
  ChevronRight,
  Shield,
  RefreshCw,
  Download,
  Wallet,
  Smartphone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  usePageTitle('My Membership', 'Manage your Advanta Plus membership and access your digital membership card');
  const { user, logout, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [dynamicCode, setDynamicCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(30);
  const walletCardRef = useRef(null);

  useEffect(() => {
    fetchMembership();
  }, []);

  // Generate dynamic code that changes every 30 seconds
  useEffect(() => {
    if (membership && showQR) {
      const generateCode = () => {
        const timestamp = Math.floor(Date.now() / 30000); // Changes every 30 sec
        const code = `${membership.id}:${timestamp}:${user.id}`;
        setDynamicCode(code);
        setTimeLeft(30 - (Math.floor(Date.now() / 1000) % 30));
      };
      
      generateCode();
      const interval = setInterval(() => {
        generateCode();
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [membership, showQR, user]);

  const fetchMembership = async () => {
    try {
      const response = await axios.get(`${API}/membership/active`, getAuthHeader());
      setMembership(response.data);
    } catch (error) {
      console.error("Failed to fetch membership:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isExpired = membership && new Date(membership.expiry_date) < new Date();

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-2xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && (
              <Link to="/admin">
                <Button variant="ghost" className="text-[#D4AF37]" data-testid="admin-link">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Button variant="ghost" onClick={handleLogout} className="text-white/60 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl text-white mb-2">
            {t('dashboard.welcome')}, <span className="text-[#D4AF37]">{user?.first_name}</span>
          </h1>
          <p className="text-white/50">{t('dashboard.manage_membership')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Membership Card */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="membership-card p-8 flex items-center justify-center">
                <span className="text-white/50">{t('common.loading')}</span>
              </div>
            ) : membership && !isExpired ? (
              <div className="membership-card p-8 relative" data-testid="membership-card">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5DEB3] to-[#B8860B]" />
                
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <span className="text-[#D4AF37] text-sm tracking-widest uppercase">{t('dashboard.digital_membership')}</span>
                    <h2 className="font-['Playfair_Display'] text-3xl text-white mt-2">Advanta Plus</h2>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase ${
                    membership.status === "active" 
                      ? "bg-[#047857]/20 text-[#047857] border border-[#047857]/30" 
                      : "bg-red-500/20 text-red-400 border border-red-500/30"
                  }`}>
                    {membership.status === "active" ? t('dashboard.active') : t('dashboard.expired')}
                  </span>
                </div>

                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-white/50 text-sm mb-1">{t('dashboard.member')}</p>
                    <p className="text-white text-xl">{user?.first_name} {user?.last_name}</p>
                    <p className="font-mono text-[#D4AF37] text-sm mt-2" data-testid="membership-id">
                      {membership.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                  
                  {/* QR Code on card */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="bg-white p-2 rounded-lg cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setShowQR(true)}
                      data-testid="card-qr-code"
                    >
                      <QRCode 
                        value={`ADVANTA:${membership.id}:${user?.id}`}
                        size={80}
                        fgColor="#020617"
                      />
                    </div>
                    <p className="text-white/40 text-xs mt-2">Tap to enlarge</p>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#D4AF37]/10">
                  <div className="text-left">
                    <p className="text-white/50 text-sm mb-1">{t('dashboard.valid_until')}</p>
                    <p className="text-white text-lg" data-testid="expiry-date">
                      {format(new Date(membership.expiry_date), "MMM d, yyyy")}
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShowWalletModal(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-[#D4AF37]/30"
                      data-testid="wallet-btn"
                      title={t('dashboard.add_to_wallet')}
                    >
                      <Wallet className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-white text-sm hidden sm:inline">Wallet</span>
                    </button>
                    <button 
                      onClick={() => setShowQR(!showQR)}
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#D4AF37] hover:bg-[#F5DEB3] transition-colors"
                      data-testid="show-qr-btn"
                      title={t('dashboard.show_qr')}
                    >
                      <QrCode className="w-4 h-4 text-[#020617]" />
                      <span className="text-[#020617] text-sm font-medium hidden sm:inline">Show QR</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="membership-card p-8 flex flex-col items-center justify-center text-center">
                <CreditCard className="w-16 h-16 text-[#D4AF37]/30 mb-4" />
                <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">
                  {isExpired ? t('dashboard.membership_expired') : t('dashboard.no_membership')}
                </h3>
                <p className="text-white/50 mb-6">
                  {isExpired 
                    ? t('dashboard.renew_desc')
                    : t('dashboard.get_card_desc')
                  }
                </p>
                <Link to="/membership">
                  <Button className="btn-gold px-8 py-4 rounded" data-testid="get-membership-btn">
                    {isExpired ? t('dashboard.renew') : t('dashboard.get_card')}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {/* QR Code Modal */}
            {showQR && membership && (
              <div 
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
                onClick={() => setShowQR(false)}
              >
                <div 
                  className="bg-[#0B0F19] rounded-2xl p-8 max-w-sm w-full text-center border border-[#D4AF37]/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">Your Membership QR</h3>
                  
                  {/* Dynamic Timer */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <RefreshCw className={`w-4 h-4 text-[#D4AF37] ${timeLeft <= 5 ? 'animate-spin' : ''}`} />
                    <span className="text-white/50 text-sm">
                      Refreshes in <span className={`font-mono ${timeLeft <= 5 ? 'text-red-400' : 'text-[#D4AF37]'}`}>{timeLeft}s</span>
                    </span>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl inline-block" data-testid="qr-code-display">
                    <QRCode
                      value={`ADVANTA:${membership.id}:${user?.id}`}
                      size={200}
                      fgColor="#020617"
                    />
                  </div>
                  
                  {/* Live Verification Code */}
                  <div className="mt-4 bg-[#D4AF37]/10 rounded-lg p-3">
                    <p className="text-white/50 text-xs mb-1">Verification Code</p>
                    <p className="font-mono text-[#D4AF37] text-2xl tracking-widest" data-testid="verification-code">
                      {dynamicCode.split(':')[1]?.slice(-4) || '----'}
                    </p>
                  </div>
                  
                  <p className="text-white/50 text-sm mt-4">
                    Show this live screen to staff - screenshots won't work
                  </p>
                  
                  <div className="flex gap-3 mt-6">
                    <Button 
                      onClick={() => setShowQR(false)}
                      className="btn-gold-outline flex-1 py-3 rounded"
                    >
                      Close
                    </Button>
                    <Button 
                      onClick={() => { setShowQR(false); setShowWalletModal(true); }}
                      className="btn-gold flex-1 py-3 rounded"
                      data-testid="add-to-wallet-btn"
                    >
                      <Wallet className="w-4 h-4 mr-2" />
                      Add to Wallet
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Pass Modal */}
            {showWalletModal && membership && (
              <div 
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
                onClick={() => setShowWalletModal(false)}
              >
                <div 
                  className="bg-[#0B0F19] rounded-2xl p-6 max-w-md w-full border border-[#D4AF37]/20 my-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-['Playfair_Display'] text-xl text-white">Add to Wallet</h3>
                    <button onClick={() => setShowWalletModal(false)} className="text-white/50 hover:text-white">
                      ✕
                    </button>
                  </div>
                  
                  {/* Wallet Card Preview */}
                  <div 
                    ref={walletCardRef}
                    className="bg-gradient-to-br from-[#020617] via-[#0B0F19] to-[#020617] rounded-xl p-5 border border-[#D4AF37]/30 mb-4"
                    style={{ aspectRatio: '1.6' }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
                          <span className="text-[#020617] font-bold text-sm">A+</span>
                        </div>
                        <span className="font-['Playfair_Display'] text-lg text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
                      </div>
                      <span className="text-[#D4AF37] text-xs font-medium bg-[#D4AF37]/10 px-2 py-1 rounded">
                        {membership.plan_name}
                      </span>
                    </div>
                    
                    <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <p className="text-white/50 text-xs mb-1">Member</p>
                        <p className="text-white font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                        
                        <p className="text-white/50 text-xs mt-2 mb-1">Valid Until</p>
                        <p className="text-white font-medium text-sm">
                          {format(new Date(membership.expiry_date), "MMM d, yyyy")}
                        </p>
                      </div>
                      
                      <div className="bg-white p-2 rounded-lg">
                        <QRCode 
                          value={`ADVANTA:${membership.id}:${user?.id}`}
                          size={70}
                          fgColor="#020617"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Download Instructions */}
                  <div className="space-y-3">
                    <p className="text-white/70 text-sm text-center">
                      Save this card to add it to your phone's wallet
                    </p>
                    
                    <Button 
                      onClick={async () => {
                        // Create a canvas to generate the card image
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        canvas.width = 400;
                        canvas.height = 250;
                        
                        // Draw background
                        const gradient = ctx.createLinearGradient(0, 0, 400, 250);
                        gradient.addColorStop(0, '#020617');
                        gradient.addColorStop(0.5, '#0B0F19');
                        gradient.addColorStop(1, '#020617');
                        ctx.fillStyle = gradient;
                        ctx.fillRect(0, 0, 400, 250);
                        
                        // Draw border
                        ctx.strokeStyle = '#D4AF37';
                        ctx.lineWidth = 2;
                        ctx.roundRect(5, 5, 390, 240, 15);
                        ctx.stroke();
                        
                        // Draw logo
                        ctx.fillStyle = '#D4AF37';
                        ctx.beginPath();
                        ctx.roundRect(20, 20, 35, 35, 8);
                        ctx.fill();
                        ctx.fillStyle = '#020617';
                        ctx.font = 'bold 20px Arial';
                        ctx.fillText('A', 30, 45);
                        
                        // Draw title
                        ctx.fillStyle = '#ffffff';
                        ctx.font = '20px Georgia';
                        ctx.fillText('Advanta Plus', 65, 45);
                        
                        // Draw plan badge
                        ctx.fillStyle = 'rgba(212, 175, 55, 0.2)';
                        ctx.beginPath();
                        ctx.roundRect(300, 20, 80, 25, 5);
                        ctx.fill();
                        ctx.fillStyle = '#D4AF37';
                        ctx.font = '12px Arial';
                        ctx.fillText(membership.plan_name, 315, 37);
                        
                        // Draw member info
                        ctx.fillStyle = 'rgba(255,255,255,0.5)';
                        ctx.font = '11px Arial';
                        ctx.fillText('Member', 20, 90);
                        ctx.fillStyle = '#ffffff';
                        ctx.font = '14px Arial';
                        ctx.fillText(`${user?.first_name} ${user?.last_name}`, 20, 110);
                        
                        ctx.fillStyle = 'rgba(255,255,255,0.5)';
                        ctx.font = '11px Arial';
                        ctx.fillText('Valid Until', 20, 145);
                        ctx.fillStyle = '#ffffff';
                        ctx.font = '14px Arial';
                        ctx.fillText(format(new Date(membership.expiry_date), "MMM d, yyyy"), 20, 165);
                        
                        ctx.fillStyle = 'rgba(255,255,255,0.5)';
                        ctx.font = '11px Arial';
                        ctx.fillText('Costa del Sol', 20, 200);
                        
                        // Draw QR code from the existing one
                        const qrSvg = document.querySelector('[data-testid="wallet-qr-code"]');
                        if (qrSvg) {
                          const svgData = new XMLSerializer().serializeToString(qrSvg);
                          const img = new Image();
                          img.onload = () => {
                            // White background for QR
                            ctx.fillStyle = '#ffffff';
                            ctx.beginPath();
                            ctx.roundRect(285, 85, 95, 95, 8);
                            ctx.fill();
                            ctx.drawImage(img, 290, 90, 85, 85);
                            
                            // Download
                            const link = document.createElement('a');
                            link.download = 'advanta-membership-card.png';
                            link.href = canvas.toDataURL('image/png');
                            link.click();
                          };
                          img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                        }
                      }}
                      className="btn-gold w-full py-3 rounded-xl"
                      data-testid="download-card-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Card Image
                    </Button>
                    
                    <div className="bg-white/5 rounded-xl p-4 text-xs text-white/60 space-y-2">
                      <p className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-[#D4AF37]" />
                        <strong className="text-white/80">iPhone:</strong> Save image → Photos → Share → Add to Wallet
                      </p>
                      <p className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-[#D4AF37]" />
                        <strong className="text-white/80">Android:</strong> Save image → Google Pay → Passes → Add with photo
                      </p>
                    </div>
                  </div>
                  
                  {/* Hidden QR for rendering */}
                  <div className="hidden">
                    <QRCode 
                      value={`ADVANTA:${membership.id}:${user?.id}`}
                      size={85}
                      fgColor="#020617"
                      data-testid="wallet-qr-code"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10">
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-4">{t('dashboard.quick_actions')}</h3>
              <div className="space-y-3">
                <Link to="/businesses" className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group">
                  <Store className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white">{t('dashboard.browse_partners')}</span>
                  <ChevronRight className="w-4 h-4 text-white/50 ml-auto group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/membership" className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group">
                  <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white">{membership ? t('dashboard.upgrade') : t('dashboard.get_card')}</span>
                  <ChevronRight className="w-4 h-4 text-white/50 ml-auto group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10">
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-4">{t('dashboard.account')}</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-white">{user?.first_name} {user?.last_name}</p>
                    <p className="text-white/50 text-sm">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-white/50 text-sm">{t('dashboard.member_since')}</p>
                    <p className="text-white">{user?.created_at ? format(new Date(user.created_at), "MMM yyyy") : "N/A"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
